/* ==========================================================================
   fk-gwp.js — gift with purchase.

   Mounts the picker into Dawn's cart drawer without modifying Dawn's markup,
   and keeps the cart honest:

     below threshold  -> meter + locked thumbnails with a "+$X" chip
     crossed          -> thumbnails unlock, "pick your gift"
     chosen           -> gift is a real line item; one gift only
     dropped below    -> gift removes itself with one gentle coral line

   The $0 price comes from an automatic discount configured in the admin, NOT
   from anything here. This file only ever adds or removes a line at quantity 1.

   Local pickup and delivery orders qualify identically — nothing here excludes
   a fulfilment method.
   ========================================================================== */

(function () {
  'use strict';

  var MOUNTED_ATTR = 'data-fk-gwp-mounted';
  var busy = false;

  function cfg() { return window.FKConfig || {}; }

  /* ------------------------------------------------------------------ */
  /* Collapse preference                                                 */
  /* ------------------------------------------------------------------ */

  /* Two persisted booleans, beside fk_store_context in localStorage.

     `armed` is the one that needs explaining: it means "this was collapsed
     while the gift was still locked, so open it once if they later qualify".
     Reopening on every crossing would make the strip impossible to keep shut -
     add an item, it is back - so the reopen is spent the first time it fires
     and never returns for a shopper who keeps dismissing it. */
  var PREF_KEY = 'fk_gwp_collapse';

  function readPref() {
    try {
      var raw = window.localStorage.getItem(PREF_KEY);
      if (!raw) return { collapsed: false, armed: false };
      var v = JSON.parse(raw);
      return { collapsed: !!v.collapsed, armed: !!v.armed };
    } catch (e) {
      return { collapsed: false, armed: false };
    }
  }

  function writePref(pref) {
    try { window.localStorage.setItem(PREF_KEY, JSON.stringify(pref)); } catch (e) {}
  }

  function applyCollapsed(root, collapsed) {
    if (!root) return;
    root.classList.toggle('is-collapsed', collapsed);
    var btn = root.querySelector('[data-fk-gwp-toggle]');
    if (btn) {
      btn.setAttribute('aria-expanded', collapsed ? 'false' : 'true');
      btn.setAttribute('aria-label', collapsed ? 'Show the free gifts' : 'Hide the free gifts');
    }
  }

  /* ------------------------------------------------------------------ */
  /* Pending and failure                                                 */
  /* ------------------------------------------------------------------ */

  function setPending(root, btn, on, label) {
    if (!root) return;
    root.querySelectorAll('[data-fk-gwp-gift]').forEach(function (b) {
      b.classList.toggle('is-pending', on && b === btn);
      /* The siblings are already inert - `busy` swallows their clicks - so
         this only makes that visible instead of silently eating the tap. */
      b.classList.toggle('is-waiting', on && b !== btn);
    });
    var line = root.querySelector('[data-fk-gwp-pendlabel]');
    if (line) line.textContent = on ? (label || '') : '';
  }

  function clearError(root) {
    if (!root) return;
    var box = root.querySelector('[data-fk-gwp-error]');
    if (box) { box.hidden = true; delete box.dataset.retryVariant; }
    var line = root.querySelector('[data-fk-gwp-pendlabel]');
    if (line) line.hidden = false;
  }

  function showError(root, btn, err) {
    if (!root) return;
    var box = root.querySelector('[data-fk-gwp-error]');
    if (!box) return;

    var name = btn ? (btn.getAttribute('data-fk-gift-title') || 'that gift') : 'that gift';
    var retry = box.querySelector('[data-fk-gwp-retry]');
    var text = box.querySelector('[data-fk-gwp-error-text]');

    /* 422 is Shopify saying it cannot be added at all - almost always the last
       one went while the drawer was open. Retrying that fails every time, and
       the section's own rule is that a sold-out gift is not rendered, so the
       tile goes rather than offering a loop. */
    var soldOut = err && err.status === 422;

    if (text) {
      text.textContent = soldOut
        ? name + ' just sold out.'
        : 'Could not add the ' + name + '.';
    }
    if (retry) retry.hidden = !!soldOut;

    if (soldOut && btn) btn.hidden = true;
    else if (btn) box.dataset.retryVariant = btn.getAttribute('data-fk-gift-variant');

    box.hidden = false;
    var line = root.querySelector('[data-fk-gwp-pendlabel]');
    if (line) line.hidden = true;
  }

  function template() { return document.querySelector('[data-fk-gwp-template]'); }

  /* ------------------------------------------------------------------ */
  /* Cart access                                                         */
  /* ------------------------------------------------------------------ */

  function fetchCart() {
    return fetch(window.Shopify && window.Shopify.routes ? window.Shopify.routes.root + 'cart.js' : '/cart.js', {
      headers: { Accept: 'application/json' }
    }).then(function (r) { return r.json(); });
  }

  function giftVariantIds() {
    var ids = [];
    document.querySelectorAll('[data-fk-gwp-gift]').forEach(function (b) {
      ids.push(Number(b.getAttribute('data-fk-gift-variant')));
    });
    return ids;
  }

  function giftLineIn(cart) {
    var ids = giftVariantIds();
    var found = null;
    (cart.items || []).forEach(function (item) {
      if (ids.indexOf(item.variant_id) !== -1) found = item;
    });
    return found;
  }

  /** Subtotal EXCLUDING any gift line, so the gift can never fund itself. */
  function qualifyingTotal(cart) {
    var ids = giftVariantIds();
    var total = 0;
    (cart.items || []).forEach(function (item) {
      if (ids.indexOf(item.variant_id) === -1) total += item.final_line_price;
    });
    return total;
  }

  /* Shopify answers a failed cart write with a 4xx AND a JSON body carrying
     `description` ("You can only add 1 of that to your cart", "sold out", ...).
     The old `.then(r => r.json())` resolved on those exactly as it did on
     success, so nothing downstream could tell a failure from a win - which is
     why there was never any point adding a .catch. Reject, and carry the
     status and the description so the UI can say which gift and why. */
  function asJson(r) {
    return r.json().then(function (body) {
      if (r.ok) return body;
      var err = new Error((body && (body.description || body.message)) || 'Cart request failed');
      err.status = r.status;
      err.body = body;
      throw err;
    });
  }

  function changeLine(variantId, quantity) {
    return fetch((window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/') + 'cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: String(variantId), quantity: quantity })
    }).then(asJson);
  }

  function addLine(variantId) {
    return fetch((window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/') + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] })
    }).then(asJson);
  }

  function money(cents) {
    var n = (cents || 0) / 100;
    return '$' + (n % 1 === 0 ? n.toFixed(0) : n.toFixed(2));
  }

  /* ------------------------------------------------------------------ */
  /* Mounting                                                            */
  /* ------------------------------------------------------------------ */

  /* THE ONE LIVE BLOCK, retained across drawer re-renders.

     Dawn's cart-drawer replaces the whole of .drawer__inner on every cart
     change, which DETACHES this node — it does not destroy it. Cloning a fresh
     copy each time (the behaviour before 2026-08-29) threw away everything the
     block knew: the dials came back at the CSS default of `0 100` and were then
     set to the new value on an element the browser had never painted, so they
     jumped instead of moving, and the gift tiles blinked out and back in.

     Keeping the reference and re-inserting the SAME element fixes both: it
     returns already populated, with its dial positions, is-met classes and
     chosen-gift state intact, so the rings animate from where they were. */
  var liveNode = null;

  function mount() {
    var drawerInner = document.querySelector('.drawer__inner');
    if (!drawerInner) return null;

    var existing = drawerInner.querySelector('[data-fk-gwp]');
    if (existing) { liveNode = existing; return existing; }

    if (!liveNode) {
      var tpl = template();
      if (!tpl) return null;
      liveNode = tpl.content.firstElementChild.cloneNode(true);
    }

    var header = drawerInner.querySelector('.drawer__header');
    if (header && header.parentNode) {
      header.parentNode.insertBefore(liveNode, header.nextSibling);
    } else {
      drawerInner.insertBefore(liveNode, drawerInner.firstChild);
    }

    drawerInner.setAttribute(MOUNTED_ATTR, 'true');
    return liveNode;
  }

  /* ------------------------------------------------------------------ */
  /* Render                                                              */
  /* ------------------------------------------------------------------ */

  function render(cart) {
    var root = mount();
    if (!root) return;

    /* TWO thresholds. `threshold` stays the GIFT number because everything
       downstream — the gift buttons, the stranded-gift removal, the chips —
       is about the gift. `ship` is the free-shipping number and only drives
       the label and the tick. Before 2026-08-28 this function read the gift
       number alone while its label promised free shipping as well, which was
       only ever correct because the two settings happened to both be $100. */
    var threshold = Number(root.getAttribute('data-fk-gwp-threshold')) || 0;
    var ship = Number(root.getAttribute('data-fk-ship-threshold')) || 0;
    var total = qualifyingTotal(cart);
    var remaining = Math.max(0, threshold - total);
    var shipRemaining = Math.max(0, ship - total);
    var unlocked = remaining === 0 && threshold > 0;
    var shipUnlocked = ship > 0 && shipRemaining === 0;
    var chosen = giftLineIn(cart);

    var dropped = root.querySelector('[data-fk-gwp-dropped]');
    var notice = root.querySelector('[data-fk-gwp-notice]');

    root.classList.toggle('is-unlocked', unlocked);

    /* ------------------------------------------------------------------
       The collapse rules. Only two transitions touch the preference:

         locked -> unlocked, while armed   open it, and spend the arming
         unlocked -> locked                open it, whatever the state,
                                           because the dropped notice below
                                           has something to say and needs
                                           somewhere to say it

       Everything else leaves the shopper's choice alone. The previous
       unlocked value is parked on the node rather than in a closure so it
       survives the drawer re-render, which hands back the same element.
       ------------------------------------------------------------------ */
    var pref = readPref();
    var prev = root.dataset.fkGwpWasUnlocked;

    if (prev === 'false' && unlocked && pref.armed) {
      pref = { collapsed: false, armed: false };
      writePref(pref);
    } else if (prev === 'true' && !unlocked && pref.collapsed) {
      pref = { collapsed: false, armed: pref.armed };
      writePref(pref);
    }

    root.dataset.fkGwpWasUnlocked = unlocked ? 'true' : 'false';
    applyCollapsed(root, pref.collapsed);

    /* ------------------------------------------------------------------
       TWO DIALS, one per reward — replaced the single scaled bar and its
       one shared sentence on 2026-08-29.

       Each dial fills toward its OWN threshold, so neither reward waits on
       the other: at $79 of a $75/$100 pair, shipping reads complete while
       the gift reads 79%. The old bar could not express that — it was
       scaled to the larger number, so shipping's completion was a tick
       passing under a fill rather than anything finishing.

       pathLength="100" on the <circle> normalises its circumference, so the
       percentage IS the dash length. Do not reintroduce 2*pi*r here: the
       radius is a CSS concern and this must not care about it.
       ------------------------------------------------------------------ */
    function dial(key, met, done, left, metText) {
      var ring = root.querySelector('[data-fk-gwp-ring="' + key + '"]');
      if (!ring) return;

      var pct = 0;
      if (done > 0) pct = Math.max(0, Math.min(100, Math.round((total / done) * 100)));

      /* INLINE STYLE, not setAttribute. `stroke-dasharray` is a presentation
         ATTRIBUTE, and in SVG those sit BELOW every CSS rule in the cascade —
         so the `stroke-dasharray: 0 100` in .fk-gwp__dialfill silently beat
         whatever this wrote and the arc stayed pinned at zero. It looked fine
         when met only because .is-met floods the disc with sage, which hides
         the arc entirely. Inline style outranks the class rule; the CSS
         declaration stays as the pre-JS default and keeps the transition. */
      var arc = ring.querySelector('[data-fk-gwp-dial="' + key + '"]');
      if (arc) arc.style.strokeDasharray = pct + ' 100';

      ring.setAttribute('aria-valuenow', String(pct));
      ring.classList.toggle('is-met', met);

      var status = ring.querySelector('[data-fk-gwp-status="' + key + '"]');
      if (status) status.textContent = met ? (metText || 'Unlocked') : money(left) + ' to go';
    }

    dial('ship', shipUnlocked, ship, shipRemaining);
    /* Carries what the removed coral band used to say. Once a gift is on the
       cart there is nothing left to invite, so it goes back to Unlocked. */
    dial('gift', unlocked, threshold, remaining, chosen ? 'Unlocked' : 'Pick your gift');

    root.querySelectorAll('[data-fk-gwp-gift]').forEach(function (btn) {
      btn.disabled = !unlocked;
      var chip = btn.querySelector('[data-fk-gwp-chip]');
      if (chip) chip.textContent = '+' + money(remaining);

      var isChosen = chosen && Number(btn.getAttribute('data-fk-gift-variant')) === chosen.variant_id;
      btn.classList.toggle('is-chosen', !!isChosen);
      btn.setAttribute('aria-pressed', isChosen ? 'true' : 'false');
    });

    /* A gift left stranded below the threshold removes itself. */
    if (chosen && !unlocked) {
      if (dropped) {
        dropped.hidden = false;
        /* Sticky, like the notice below. removeGift() triggers another render
           about 200ms later, which used to hide this line again — so the gift
           vanished from the cart and the only explanation vanished with it. */
        dropped.dataset.sticky = 'true';
        /* Names the GIFT threshold specifically. It used to say "dropped below
           $100" while the label beside it talked about free shipping, which
           read as one number governing both. */
        dropped.textContent = 'Your order dropped below ' + money(threshold) +
          ' — add ' + money(remaining) + ' back to keep your free gift.';
      }
      removeGift(chosen.variant_id);
      return;
    }

    /* Clear the sticky flag only once the shopper is back above the threshold. */
    if (dropped && !dropped.dataset.sticky) dropped.hidden = true;
    if (dropped && unlocked) { delete dropped.dataset.sticky; dropped.hidden = true; }
    if (notice && !notice.dataset.sticky) notice.hidden = true;
  }

  /* ------------------------------------------------------------------ */
  /* Actions                                                             */
  /* ------------------------------------------------------------------ */

  function removeGift(variantId) {
    if (busy) return Promise.resolve();
    busy = true;
    return changeLine(variantId, 0)
      .then(function (cart) { broadcastCartChange(cart); return cart; })
      .finally(function () { busy = false; });
  }

  function chooseGift(variantId, btn) {
    if (busy) return Promise.resolve();

    var root = document.querySelector('[data-fk-gwp]');
    if (!btn && root) {
      btn = root.querySelector('[data-fk-gift-variant="' + variantId + '"]');
    }

    /* The leading fetchCart() this used to open with existed to ask the cart
       which gift was currently chosen. The DOM already knows: render() puts
       .is-chosen on that tile every time it runs. Reading it here takes a
       ~290ms round trip off every pick - about a fifth of the wait - and the
       trailing fetchCart still reconciles against the real cart, so a stale
       class costs a corrected render rather than a wrong cart. */
    var current = root ? root.querySelector('[data-fk-gwp-gift].is-chosen') : null;
    var currentId = current ? Number(current.getAttribute('data-fk-gift-variant')) : 0;

    /* Re-tapping the gift already on the cart must not add a second unit. */
    if (currentId === variantId) return Promise.resolve();

    busy = true;
    clearError(root);
    setPending(root, btn, true, currentId ? 'Swapping your gift…' : 'Adding your gift…');

    /* A removal that fails because the line is already gone is not a failure:
       the intent is "this gift is not in the cart", and it is not. Swallowing
       it matters now that a non-OK response rejects - without this, retrying
       after a failed swap tries to remove a line the first attempt already
       removed, gets a 422, and reports a second error for a non-problem. */
    var first = currentId
      ? changeLine(currentId, 0).catch(function () {})
      : Promise.resolve();

    return first
      .then(function () { return addLine(variantId); })
      .then(fetchCart)
      .then(function (cart) {
        setPending(root, btn, false);
        /* The gift is on the cart, so the picker has done its job: collapse.
           Never armed - they have seen the gift and chosen it, so there is
           nothing left to reveal later. */
        writePref({ collapsed: true, armed: false });
        applyCollapsed(root, true);
        broadcastCartChange(cart);
        return cart;
      })
      .catch(function (err) {
        setPending(root, btn, false);

        /* A swap removes the old gift BEFORE adding the new one, so an add
           that fails here has already left the shopper with no gift at all
           while the old tile still shows as chosen. Clearing the spinner is
           not a revert on its own - the cart moved.

           Put the old gift back, then reconcile against the real cart either
           way, so the strip can never keep claiming something the cart does
           not have. Both steps are allowed to fail without masking the
           original error, which is the one worth showing. */
        var restore = currentId ? addLine(currentId).catch(function () {}) : Promise.resolve();

        return restore
          .then(fetchCart)
          .then(function (cart) { render(cart); })
          .catch(function () {})
          .then(function () { showError(root, btn, err); });
      })
      .finally(function () { busy = false; });
  }

  /**
   * Nobody should lose a gift through inattention: if the shopper qualifies and
   * heads to checkout without picking, add the most-stocked option and say so.
   */
  function autoAddIfNeeded() {
    return fetchCart().then(function (cart) {
      var root = document.querySelector('[data-fk-gwp]');
      if (!root) return cart;

      var threshold = Number(root.getAttribute('data-fk-gwp-threshold')) || 0;
      if (qualifyingTotal(cart) < threshold || threshold === 0) return cart;
      if (giftLineIn(cart)) return cart;

      var best = null;
      root.querySelectorAll('[data-fk-gwp-gift]').forEach(function (btn) {
        var stock = Number(btn.getAttribute('data-fk-gift-inventory')) || 0;
        if (!best || stock > best.stock) {
          best = { id: Number(btn.getAttribute('data-fk-gift-variant')), stock: stock };
        }
      });

      if (!best) return cart;

      return addLine(best.id).then(fetchCart).then(function (updated) {
        var notice = root.querySelector('[data-fk-gwp-notice]');
        if (notice) {
          notice.hidden = false;
          notice.dataset.sticky = 'true';
          notice.textContent = 'We added your free gift — tap Change to swap.';
        }
        broadcastCartChange(updated);
        return updated;
      });
    });
  }

  /* Let Dawn know the cart changed so its own sections re-render. */
  /* NAMED broadcastCartChange, not publish. It used to be `function publish`,
     which SHADOWED Dawn's global publish() from pubsub.js inside its own body:
     `typeof publish === 'function'` then tested this function rather than
     Dawn's, passed, and the next line called ITSELF with (eventName, data)
     instead of Dawn's — unbounded recursion ending in a stack overflow the
     moment a gift was chosen, and Dawn never hearing about the cart change
     either. It has never fired only because no gift products exist yet (F4),
     so the picker renders zero tiles and the path is unreachable. */
  function broadcastCartChange(cart) {
    if (typeof PUB_SUB_EVENTS !== 'undefined' && typeof publish === 'function') {
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'fk-gwp', cart: cart });
    }
    document.dispatchEvent(new CustomEvent('fk:cart-updated', { detail: cart }));
    refresh();
  }

  function refresh() {
    fetchCart().then(render).catch(function () { /* cart unavailable — leave the drawer alone */ });
  }

  /* ------------------------------------------------------------------ */
  /* Wiring                                                              */
  /* ------------------------------------------------------------------ */

  document.addEventListener('click', function (e) {
    /* Collapse / expand. Arming only on a locked collapse is what makes the
       reopen fire once - see readPref. */
    var toggle = e.target.closest('[data-fk-gwp-toggle]');
    if (toggle) {
      e.preventDefault();
      var tRoot = toggle.closest('[data-fk-gwp]');
      if (!tRoot) return;
      var collapsing = !tRoot.classList.contains('is-collapsed');
      writePref({ collapsed: collapsing, armed: collapsing && !tRoot.classList.contains('is-unlocked') });
      applyCollapsed(tRoot, collapsing);
      clearError(tRoot);
      return;
    }

    var retry = e.target.closest('[data-fk-gwp-retry]');
    if (retry) {
      e.preventDefault();
      var rRoot = retry.closest('[data-fk-gwp]');
      var box = rRoot && rRoot.querySelector('[data-fk-gwp-error]');
      var again = box && box.dataset.retryVariant;
      clearError(rRoot);
      if (again) chooseGift(Number(again));
      return;
    }

    var gift = e.target.closest('[data-fk-gwp-gift]');
    if (gift && !gift.disabled) {
      e.preventDefault();
      chooseGift(Number(gift.getAttribute('data-fk-gift-variant')), gift);
      return;
    }

    /* Intercept checkout just long enough to honour the fallback. */
    var checkout = e.target.closest('[name="checkout"], .cart__checkout-button');
    if (checkout) {
      var root = document.querySelector('[data-fk-gwp]');
      if (!root || root.dataset.autoAdded) return;
      root.dataset.autoAdded = 'true';
      e.preventDefault();
      autoAddIfNeeded().finally(function () {
        /* Re-fire the REAL button rather than form.submit(): the programmatic
           call omits the submitter, so the POST to /cart carries no `checkout`
           field and Shopify bounces the shopper straight back to the cart.
           The autoAdded guard above is already set, so this second click
           falls through to the browser's native submit. */
        checkout.click();
      });
    }
  });

  /* Dawn broadcasts cart updates through pubsub; also poll on drawer open. */
  /* This was an empty stub, on the assumption everything went through publish().
     It does not: fk-ui.js's ajaxAdd — which is what BOTH the product-card and
     PDP add buttons use, since neither form is wrapped in Dawn's <product-form>
     — dispatches this event and never publishes cartUpdate. The meter only
     survived an add by accident, because renderContents() wipes the drawer and
     the MutationObserver below re-mounts the block against a fresh cart. Listen
     properly so the meter does not depend on that side effect; a double refresh
     is harmless. */
  document.addEventListener('fk:cart-updated', function () { refresh(); });

  /* Bare binding, not window.* — see the note in fk-ui.js. */
  if (typeof PUB_SUB_EVENTS !== 'undefined' && typeof subscribe === 'function') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, function (event) {
      if (event && event.source === 'fk-gwp') return;
      refresh();
    });
  }

  /* Re-insert SYNCHRONOUSLY, then refresh.

     The old order called refresh() alone, which awaits a /cart.js round trip
     before it mounts anything — so the block stayed missing for the whole
     request and the drawer visibly collapsed and sprang back. Mounting first
     puts the populated node back inside the same frame the drawer re-rendered
     in; the fetch that follows only updates numbers. */
  var drawerObserver = new MutationObserver(function () {
    if (!document.querySelector('.drawer__inner')) return;
    if (document.querySelector('[data-fk-gwp]')) return;
    mount();
    refresh();
  });

  function init() {
    if (!template()) return;
    refresh();
    var drawer = document.querySelector('cart-drawer, .cart-drawer');
    if (drawer) drawerObserver.observe(drawer, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
